/**
 * Lightweight Markdown Renderer
 * Handles common markdown patterns for chat messages
 * Includes syntax highlighting for code blocks
 */

import { highlight } from './highlighter.js';

/**
 * Render markdown text to HTML
 * @param {string} text - Raw markdown text
 * @returns {string} HTML string
 */
export function renderMarkdown(text) {
  if (!text) return '';

  let html = text;

  // Escape HTML first (prevent XSS)
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks (```language\n...\n```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const langLabel = lang ? `<span class="code-lang">${lang}</span>` : '';
    const highlighted = lang ? highlight(code.trim(), lang) : code.trim();
    return `<div class="code-block">${langLabel}<button class="code-copy-btn" data-action="copy-code" title="Copy">Copy</button><pre><code class="${lang}">${highlighted}</code></pre></div>`;
  });

  // Inline code
  html = html.replace(/`([^`\n]+)`/g, '<code class="inline-code">$1</code>');

  // Bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');

  // Strikethrough
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  // Headers (only at start of line)
  html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>');

  // Horizontal rules
  html = html.replace(/^---$/gm, '<hr>');

  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');

  // Tables (must be before line-break processing)
  html = html.replace(/((?:^\|.+\|[ \t]*\n)+)/gm, (tableBlock) => {
    const rows = tableBlock.trim().split('\n');
    if (rows.length < 2) return tableBlock;

    // Check if second row is a separator row (|---|---|)
    const isSeparator = /^\|[\s\-:]+(\|[\s\-:]+)+\|?$/.test(rows[1]);
    if (!isSeparator) return tableBlock;

    let tableHtml = '<table class="md-table"><thead><tr>';
    // Header row
    const headers = rows[0].split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1);
    headers.forEach(h => { tableHtml += `<th>${h.trim()}</th>`; });
    tableHtml += '</tr></thead><tbody>';

    // Data rows (skip header and separator)
    for (let r = 2; r < rows.length; r++) {
      const cells = rows[r].split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1);
      tableHtml += '<tr>';
      cells.forEach(c => { tableHtml += `<td>${c.trim()}</td>`; });
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table>';
    return tableHtml;
  });

  // Unordered lists
  html = html.replace(/^[*-] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Links (sanitize URLs — only allow http/https protocols)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => {
    const safeUrl = /^https?:\/\//i.test(url) ? url : '#';
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });

  // Line breaks (double newline = paragraph, single = br)
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');

  // Wrap in paragraphs (but not if already wrapped in block elements)
  if (!html.startsWith('<h') && !html.startsWith('<div') && !html.startsWith('<ul') && !html.startsWith('<blockquote')) {
    html = `<p>${html}</p>`;
  }

  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');

  return html;
}

/**
 * Handle copy code button clicks
 */
export function handleCodeCopy(e) {
  const btn = e.target.closest('[data-action="copy-code"]');
  if (!btn) return;

  const codeBlock = btn.closest('.code-block');
  const code = codeBlock?.querySelector('code')?.textContent;
  if (!code) return;

  navigator.clipboard.writeText(code).then(() => {
    btn.textContent = 'Copied!';
    setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
  });
}
